import { useState } from "react";
import { MessageSquare, Phone, X, MessageCircle } from "lucide-react";
import { AIChatWidget } from "./AIChatWidget";
import { useToast } from "@/components/ui/use-toast";
import deniseChatIcon from "@/assets/denise-chat-icon.png";

export const ContactSupportWidget = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const { toast } = useToast();

  const phoneNumber = "+12102967409";
  const whatsAppNumber = "12102967409";
  const whatsAppUrl = `https://wa.me/${whatsAppNumber}`;

  const handleAIChatClick = () => {
    setIsMenuOpen(false);
    setShowAIChat(true);
  };

  const handleCloseAIChat = () => {
    setShowAIChat(false);
  };

  const handleWhatsAppClick = () => {
    // Try to open WhatsApp in new window
    const newWindow = window.open(whatsAppUrl, '_blank', 'noopener,noreferrer');
    
    // If blocked (returns null), show toast with link
    if (!newWindow || newWindow.closed) {
      toast({
        title: "WhatsApp",
        description: (
          <div className="flex flex-col gap-2">
            <p>Message us at: (210) 296-7409</p>
            <a 
              href={whatsAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-500 underline"
            >
              Open WhatsApp
            </a>
          </div>
        ),
      });
    }
  };

  return (
    <>
      {/* AI Chat Window - controlled externally */}
      <AIChatWidget 
        isOpen={showAIChat} 
        onClose={handleCloseAIChat}
        onInteraction={() => {}}
      />

      {/* Contact & Support Hub - only show when AI chat is closed */}
      {!showAIChat && (
        <div className="fixed bottom-24 lg:bottom-6 right-4 sm:right-6 z-[99999] bounce-in">
          <div className="flex items-end gap-3">
            {/* "Denise AI Assistant" Label - only shows when menu is CLOSED */}
            {!isMenuOpen && (
              <div className="hidden sm:block animate-in fade-in slide-in-from-right-2 duration-500 mb-3">
                <div className="bg-card text-primary px-3 py-2 rounded-xl shadow-lg border border-primary/30 whitespace-nowrap relative">
                  <span className="font-medium text-xs sm:text-sm">Denise AI Assistant</span>
                  {/* Triangle pointer at right */}
                  <div className="absolute top-1/2 -translate-y-1/2 left-full -ml-1.5 w-3 h-3 bg-card transform rotate-45 border-t border-r border-primary/30"></div>
                </div>
              </div>
            )}

            <div className="flex flex-col items-end gap-3">
              {/* Expanded Menu Options */}
              {isMenuOpen && (
                <div className="flex flex-col gap-3 animate-in slide-in-from-bottom-4 fade-in duration-200">
                  
                  {/* Chat with AI Button */}
                  <button
                    onClick={handleAIChatClick}
                    className="flex items-center gap-3 bg-primary text-primary-foreground px-4 py-3 rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
                    aria-label="Chat with Denise AI Assistant"
                  >
                    <img 
                      src={deniseChatIcon} 
                      alt="Denise AI Assistant" 
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="font-medium">Chat with AI</span>
                    <MessageSquare size={20} />
                  </button>

                  {/* WhatsApp Button */}
                  <button
                    onClick={handleWhatsAppClick}
                    className="flex items-center gap-3 px-4 py-3 rounded-full shadow-lg transition-all hover:scale-105 text-white cursor-pointer"
                    style={{ backgroundColor: "#25D366" }}
                    aria-label="Chat with us on WhatsApp"
                  >
                    <span className="font-medium">WhatsApp</span>
                    <MessageCircle size={20} />
                  </button>

                  {/* Call Now Button */}
                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3 bg-card text-card-foreground border border-border px-4 py-3 rounded-full shadow-lg hover:bg-accent transition-all hover:scale-105"
                    aria-label="Call us at 210-296-7409"
                  >
                    <span className="font-medium">Call Now</span>
                    <Phone size={20} />
                  </a>
                </div>
              )}

              {/* Main Toggle Button with Pulsing Gold Glow - Denise Avatar */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`rounded-full transition-all duration-300 relative z-10 overflow-hidden ${
                  isMenuOpen 
                    ? "bg-destructive p-4 rotate-90 shadow-lg" 
                    : "border-3 border-card shadow-[0_4px_8px_rgba(0,0,0,0.3)] hover:scale-110"
                }`}
                style={!isMenuOpen ? {
                  animation: 'pulse-glow 2s ease-in-out infinite',
                  width: '60px',
                  height: '60px'
                } : undefined}
                aria-label={isMenuOpen ? "Close contact menu" : "Chat with Denise AI Assistant"}
              >
                {isMenuOpen ? (
                  <X size={24} className="text-destructive-foreground" />
                ) : (
                  <img 
                    src={deniseChatIcon} 
                    alt="Denise AI Assistant" 
                    className="w-full h-full object-cover rounded-full"
                  />
                )}
              </button>
            </div>
          </div>
          
          {/* Pulse glow and bounce keyframes - Gold color */}
          <style>{`
            @keyframes pulse-glow {
              0%, 100% {
                box-shadow: 0 0 30px rgba(212, 175, 55, 0.7), 0 0 60px rgba(212, 175, 55, 0.4);
              }
              50% {
                box-shadow: 0 0 50px rgba(212, 175, 55, 1), 0 0 80px rgba(212, 175, 55, 0.6), 0 0 100px rgba(212, 175, 55, 0.3);
              }
            }
            @keyframes bounce-in {
              0% {
                transform: scale(0);
                opacity: 0;
              }
              50% {
                transform: scale(1.15);
              }
              70% {
                transform: scale(0.95);
              }
              100% {
                transform: scale(1);
                opacity: 1;
              }
            }
            .bounce-in {
              animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default ContactSupportWidget;